import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { requests } from "../services/http-common";
import { Employee, EmployeeRoles } from "../models/Employee";

/** A dropdown row: PrimeReact's shape, plus the role callers filter on. */
export type EmployeeOption = {
  label: string;
  value: string;
  role: EmployeeRoles;
};

/**
 * The one employee list the app builds its people pickers from.
 *
 * The query key stays `employee-list` so every caller shares a single cache
 * entry and a single `/user/allEmployee` fetch - the job form and the
 * schedule page both want the same list, and fetching it twice would be
 * visible as two requests in flight on any page that mounts both.
 *
 * `employees` is handed back alongside the options because a name is not
 * always wanted as a label: the schedule's "By employee" view needs a row
 * title for someone who has no shift to take a name from.
 */
export const useAllEmployeeOptions = (): {
  employees: Employee[];
  options: EmployeeOption[];
} => {
  const { data: employeesPayload } = useQuery({
    queryKey: ["employee-list"],
    queryFn: () => {
      return requests.get("/user/allEmployee");
    },
  });

  const employees: Employee[] = useMemo(
    () => (Array.isArray(employeesPayload?.result) ? employeesPayload.result : []),
    [employeesPayload]
  );

  const options = useMemo(
    () =>
      employees
        .map((employee: any) => ({
          label: `${employee.firstName} ${employee.lastName}`,
          value: employee.id,
          role: employee.role,
        }))
        .sort((a: EmployeeOption, b: EmployeeOption) =>
          a.label.localeCompare(b.label)
        ),
    [employees]
  );

  return { employees, options };
};

/** The subset of the list whose role is one of `roles`. */
export const employeesInRoles = <T extends { role: EmployeeRoles | string }>(
  people: T[],
  roles: EmployeeRoles[]
): T[] => people.filter((person) => roles.includes(person.role as EmployeeRoles));
